import { useEffect, useRef, useState } from "react";
import {
  Bold, Italic, Underline as UIcon, AlignLeft, AlignCenter, AlignRight, AlignJustify,
  Type, ChevronDown, ChevronUp, Keyboard, Scale, Columns2, IndentIncrease,
} from "lucide-react";

/**
 * Rich-text editor for practice modules that need formatting fidelity
 * (Legal, Typing). Uses `contentEditable` + document.execCommand — old
 * but ubiquitous and works for the six commands we need here.
 *
 * Props:
 *   value       — HTML string (controlled from parent)
 *   onChange    — (html: string, plainText: string) => void
 *   onKeyDown   — keyboard-sound handler passthrough
 *   disabled    — locks editing
 *   placeholder — shown when empty
 *   testId      — data-testid on the editable element
 *   showControls (default true) — toolbar visibility toggle
 *   size        — "default" | "legal". "legal" gives a full A4-ish canvas
 *                 (~880px tall) with generous padding so admins can paste an
 *                 entire judgement without cramming.
 *   keyControls — { capsLockWarning, spaceBar, controlKeys, functionKeys }
 *                 Any flag = false blocks the matching keystroke. Defaults to
 *                 all-enabled (matches previous behaviour).
 */
export default function RichTextEditor({
  value = "",
  onChange,
  onKeyDown,
  disabled = false,
  placeholder = "Start typing here…",
  testId = "rich-text-editor",
  showControls: initialShow = true,
  size = "default",
  keyControls,
  layout = "normal",
  hideDefaultToolbar = false,  // When true, the caller renders its own toolbar and this component
                                // shows ONLY the editable canvas (no top strip, no format bar).
                                // Used by Legal Practice where the timer + toolbar live above the
                                // editor in a page-level header.
  wideCanvas = false,          // When true, the .legal-page inner canvas stretches full-width instead
                                // of the default 960px paper. Used by full-screen practice layout.
  scrollRef,           // Ref that gets attached to the inner scroll surface when
                       // layout="fill". Lets LegalPractice's sync-scroll drive
                       // the two panes in lock-step (source pane + editor pane).
}) {
  const kc = {
    capsLockWarning: true,
    spaceBar: true,
    controlKeys: true,
    functionKeys: true,
    ...(keyControls || {}),
  };
  const ref = useRef(null);
  const [showToolbar, setShowToolbar] = useState(initialShow);
  const [caps, setCaps] = useState(false);
  const [active, setActive] = useState({ bold: false, italic: false, underline: false, align: "left" });

  // Sync from parent value (only if editor doesn't have focus)
  useEffect(() => {
    if (!ref.current) return;
    if (document.activeElement !== ref.current && ref.current.innerHTML !== value) {
      ref.current.innerHTML = value || "";
    }
  }, [value]);

  const emit = () => {
    if (!ref.current) return;
    const html = ref.current.innerHTML;
    const text = ref.current.innerText;
    onChange?.(html, text);
  };

  const exec = (cmd, arg) => {
    document.execCommand(cmd, false, arg);
    ref.current?.focus();
    emit();
    updateActive();
  };

  // Inject a court-format "Cause Title" row — name (left) …… role (right).
  // Renders identically wherever the resulting HTML is displayed, because the
  // .cause-title styling lives in a single global stylesheet (index.css).
  const insertCauseTitle = () => {
    const party = window.prompt("Party name (e.g. Wasim Rahman)", "");
    if (party == null) return;
    const role = window.prompt("Role (Appellant(s), Respondent(s), Petitioner(s)…)", "Appellant(s)");
    if (role == null) return;
    const escape = (s) => String(s).replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
    const html = `<div class="cause-title"><span class="party">${escape(party)}</span><span class="leader"></span><span class="role">…${escape(role)}</span></div>`;
    document.execCommand("insertHTML", false, html);
    ref.current?.focus();
    emit();
  };

  // Inject a 2-column Counsel block. Each side stays in its own column so long
  // advocate names never stagger into the other column.
  const insertCounselColumns = () => {
    const left = window.prompt("Counsel for the Appellant(s)", "Sanjay Kumar Upadhyay");
    if (left == null) return;
    const right = window.prompt("Counsel for the Respondent(s)", "");
    if (right == null) return;
    const escape = (s) => String(s).replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
    const html = `<div class="counsel-columns"><div><h4>For the Appellant(s)</h4><div>${escape(left)}</div></div><div><h4>For the Respondent(s)</h4><div>${escape(right)}</div></div></div>`;
    document.execCommand("insertHTML", false, html);
    ref.current?.focus();
    emit();
  };

  const updateActive = () => {
    try {
      setActive({
        bold: document.queryCommandState("bold"),
        italic: document.queryCommandState("italic"),
        underline: document.queryCommandState("underline"),
        align:
          document.queryCommandState("justifyCenter") ? "center" :
          document.queryCommandState("justifyRight") ? "right" :
          document.queryCommandState("justifyFull") ? "justify" : "left",
      });
    } catch { /* ignore */ }
  };

  // Insert a real tab character at the caret. Keeps the browser default (which
  // moves focus to the next form element) suppressed — students expect Tab to
  // indent inside the typing canvas, not jump out of it. The diff engine
  // tokenises on `\S+` so whitespace (tabs OR spaces) is never counted as a
  // typing penalty.
  const insertTab = () => {
    document.execCommand("insertText", false, "\t");
    ref.current?.focus();
    emit();
  };

  const handleKey = (e) => {
    if (e.getModifierState) setCaps(e.getModifierState("CapsLock"));
    onKeyDown?.(e);

    // Tab key — always intercept before default focus-shift. Shift+Tab is left
    // to the browser's default (out of the editor) so accessibility isn't lost.
    if (e.key === "Tab" && !e.shiftKey && !disabled) {
      e.preventDefault();
      insertTab();
      return;
    }

    // Key-control restrictions (student-configured).
    // Function keys (F1–F12)
    if (!kc.functionKeys && /^F(?:[1-9]|1[0-2])$/.test(e.key)) {
      e.preventDefault(); e.stopPropagation(); return;
    }
    // Spacebar lock
    if (!kc.spaceBar && (e.key === " " || e.code === "Space")) {
      e.preventDefault(); return;
    }
    // Ctrl/Cmd shortcut lock — blocks EVERY Ctrl/Cmd combination when off.
    if (!kc.controlKeys && (e.ctrlKey || e.metaKey) && e.key !== "Control" && e.key !== "Meta") {
      e.preventDefault(); return;
    }

    // Keyboard shortcuts (only when Ctrl combos are allowed)
    if (kc.controlKeys && (e.ctrlKey || e.metaKey) && !e.altKey) {
      const k = e.key.toLowerCase();
      if (k === "b") { e.preventDefault(); exec("bold"); return; }
      if (k === "i") { e.preventDefault(); exec("italic"); return; }
      if (k === "u") { e.preventDefault(); exec("underline"); return; }
      if (k === "j") { e.preventDefault(); exec("justifyFull"); return; }      // Justify
      if (k === "e") { e.preventDefault(); exec("justifyCenter"); return; }    // Center
      if (k === "l") { e.preventDefault(); exec("justifyLeft"); return; }      // Left
      if (k === "r") { e.preventDefault(); exec("justifyRight"); return; }     // Right
    }
  };

  const tbBtn = (label, active, onClick, testid, Icon) => (
    <button
      type="button"
      onClick={onClick}
      onMouseDown={(e) => e.preventDefault()}
      data-testid={testid}
      aria-pressed={active}
      title={label}
      className={`w-9 h-9 flex items-center justify-center rounded-md border transition-colors ${
        active
          ? "border-brand-gold bg-brand-gold text-background"
          : "border-border bg-secondary/40 text-muted-foreground hover:border-brand-gold/40 hover:text-foreground"
      }`}
    >
      <Icon className="w-4 h-4" />
    </button>
  );

  const fill = layout === "fill";
  return (
    <div className={`w-full ${fill ? "h-full flex flex-col min-h-0" : ""}`} data-testid={`${testId}-wrapper`}>
      {/* Toolbar — sticky top so it stays reachable when the editable canvas
          scrolls inside a fullscreen pane. In fill layout the toolbar sits
          OUTSIDE the scroll surface as a flex-shrink-0 header. */}
      {!hideDefaultToolbar && (
      <div className={`flex items-center justify-between gap-2 flex-wrap px-1 py-1 ${fill ? "flex-shrink-0 border-b border-border/60 bg-background/95" : "mb-2 sticky top-0 z-20 bg-background/95 backdrop-blur -mx-1"}`}>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setShowToolbar((v) => !v)}
            className="inline-flex items-center gap-1.5 px-3 h-9 rounded-md border border-border bg-secondary/40 text-[10px] uppercase tracking-widest font-bold text-muted-foreground hover:text-foreground"
            data-testid={`${testId}-toggle-controls`}
          >
            <Type className="w-3.5 h-3.5" />
            {showToolbar ? "Hide controls" : "Show controls"}
            {showToolbar ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          </button>
          {caps && kc.capsLockWarning && (
            <span
              data-testid={`${testId}-capslock-indicator`}
              className="inline-flex items-center gap-1.5 px-3 h-9 rounded-md border border-brand-gold bg-brand-gold/10 text-brand-gold text-[10px] uppercase tracking-widest font-bold"
              title="Caps Lock is ON"
            >
              <Keyboard className="w-3.5 h-3.5" /> Caps Lock ON
            </span>
          )}
        </div>
      </div>
      )}

      {!hideDefaultToolbar && showToolbar && (
        <div className={`flex items-center gap-1 flex-wrap p-2 ${fill ? "flex-shrink-0 border-b border-border/60 bg-secondary/40" : "mb-2 border border-border rounded-lg bg-secondary/60 sticky top-11 z-10"} backdrop-blur`} data-testid={`${testId}-toolbar`}>
          {tbBtn("Bold (Ctrl+B)", active.bold, () => exec("bold"), `${testId}-bold`, Bold)}
          {tbBtn("Italic (Ctrl+I)", active.italic, () => exec("italic"), `${testId}-italic`, Italic)}
          {tbBtn("Underline (Ctrl+U)", active.underline, () => exec("underline"), `${testId}-underline`, UIcon)}
          <div className="w-px h-6 bg-border mx-1" />
          {tbBtn("Left align", active.align === "left", () => exec("justifyLeft"), `${testId}-align-left`, AlignLeft)}
          {tbBtn("Center align", active.align === "center", () => exec("justifyCenter"), `${testId}-align-center`, AlignCenter)}
          {tbBtn("Right align", active.align === "right", () => exec("justifyRight"), `${testId}-align-right`, AlignRight)}
          {tbBtn("Justify", active.align === "justify", () => exec("justifyFull"), `${testId}-align-justify`, AlignJustify)}
          <div className="w-px h-6 bg-border mx-1" />
          {tbBtn("Insert Tab (⇥)", false, insertTab, `${testId}-tab`, IndentIncrease)}
          {size === "legal" && <div className="w-px h-6 bg-border mx-1" />}
          {size === "legal" && tbBtn("Cause title (Name …… Role)", false, insertCauseTitle, `${testId}-cause-title`, Scale)}
          {size === "legal" && tbBtn("Counsel columns (2-col)", false, insertCounselColumns, `${testId}-counsel-columns`, Columns2)}
        </div>
      )}

      {fill ? (
        <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto pane-scroll" data-testid={`${testId}-scroll`}>
          <div
            ref={ref}
            contentEditable={!disabled}
            suppressContentEditableWarning
            onInput={emit}
            onKeyDown={handleKey}
            onKeyUp={(e) => { if (e.getModifierState) setCaps(e.getModifierState("CapsLock")); updateActive(); }}
            onMouseUp={updateActive}
            onFocus={updateActive}
            onPaste={(e) => { if (!kc.controlKeys) e.preventDefault(); }}
            onCopy={(e) => { if (!kc.controlKeys) e.preventDefault(); }}
            onCut={(e) => { if (!kc.controlKeys) e.preventDefault(); }}
            onContextMenu={(e) => { if (!kc.controlKeys) e.preventDefault(); }}
            role="textbox"
            aria-multiline="true"
            aria-label="Practice typing area"
            data-testid={testId}
            data-placeholder={placeholder}
            style={{ lineHeight: 1.55, whiteSpace: "pre-wrap" }}
            className={`legal-page ${wideCanvas ? "legal-page--full" : ""} font-mono practice-typing-area focus:outline-none focus:ring-4 focus:ring-brand-gold/30 empty:before:content-[attr(data-placeholder)] empty:before:text-muted-foreground empty:before:pointer-events-none ${
              disabled ? "opacity-70 cursor-not-allowed" : ""
            }`}
          />
        </div>
      ) : (
        <div
          ref={ref}
          contentEditable={!disabled}
          suppressContentEditableWarning
          onInput={emit}
          onKeyDown={handleKey}
          onKeyUp={(e) => { if (e.getModifierState) setCaps(e.getModifierState("CapsLock")); updateActive(); }}
          onMouseUp={updateActive}
          onFocus={updateActive}
          onPaste={(e) => { if (!kc.controlKeys) e.preventDefault(); }}
          onCopy={(e) => { if (!kc.controlKeys) e.preventDefault(); }}
          onCut={(e) => { if (!kc.controlKeys) e.preventDefault(); }}
          onContextMenu={(e) => { if (!kc.controlKeys) e.preventDefault(); }}
          role="textbox"
          aria-multiline="true"
          aria-label="Practice typing area"
          data-testid={testId}
          data-placeholder={placeholder}
          style={{ lineHeight: 1.55, whiteSpace: "pre-wrap" }}
          className={`${size === "legal" ? `legal-page ${wideCanvas ? "legal-page--full" : ""} min-h-[1344px] font-mono` : "min-h-[280px] w-full p-4 border-2 border-border bg-background text-foreground rounded-lg"} practice-typing-area focus:outline-none focus:ring-4 focus:ring-brand-gold/30 empty:before:content-[attr(data-placeholder)] empty:before:text-muted-foreground empty:before:pointer-events-none ${
            disabled ? "opacity-70 cursor-not-allowed" : ""
          }`}
        />
      )}
    </div>
  );
}
